import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  AlertTriangle,
  AlignLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
  ListChecks,
  Loader2,
  MoreHorizontal,
  Pause,
  RefreshCw,
  CircleDot,
  StopCircle,
  Trash2,
  User,
  Users,
  XCircle,
  FileText
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { apiClient } from '../lib/api'
import {
  ActivateStudentJobConflictError,
  studentAssessmentsApi,
  StudentAssessmentActivationConflictResolution,
  StudentAssessmentActivateConflictStudent,
  StudentAssessmentActivatePayload,
  StudentAssessmentAlertLevel,
  StudentAssessmentRun,
  StudentAssessmentSelectionScope,
  StudentAssessmentActivationDetails
} from '../lib/studentAssessmentsApi'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { ListToolbar } from '../components/ui/ListToolbar'
import { EmptyTableRow } from '../components/ui/EmptyState'
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, TableScrollArea } from '../components/ui/TableShell'

interface ClassItem {
  Klasse: string
}

interface GroupItem {
  id: number
  name: string
}

interface StudentOption {
  unid: string
  label: string
  className: string
}

const DEFAULT_DETAILS: StudentAssessmentActivationDetails = {
  scheduleInterval: 'monthly',
  triggerOnSchedule: true,
  triggerOnEvent: true,
  eventCooldownMinutes: 240,
  notifyTeacher: true,
  notifyParents: false,
  notifyStudents: false,
  minAlertLevel: 'yellow',
  lookbackDays: 60
}

function statusVariant(status: string) {
  if (status === 'completed') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'processing' || status === 'queued') return 'info'
  return 'neutral'
}

function alertVariant(level: StudentAssessmentAlertLevel | null) {
  if (level === 'red') return 'danger'
  if (level === 'yellow') return 'warning'
  if (level === 'green') return 'success'
  return 'neutral'
}

function TableColumnHeading({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
      <span>{label}</span>
    </div>
  )
}

function StatusGlyph({ status }: { status: string }) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden />
  }
  if (status === 'failed') {
    return <XCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
  }
  if (status === 'skipped') {
    return <CircleDot className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
  }
  if (status === 'processing' || status === 'queued') {
    return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-hidden />
  }
  return <CircleDot className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
}

function AlertLevelGlyph({ level }: { level: StudentAssessmentAlertLevel | null }) {
  if (level === 'red') {
    return <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
  }
  if (level === 'yellow') {
    return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
  }
  if (level === 'green') {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden />
  }
  return <CircleDot className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
}

function IconActionButton({
  title,
  onClick,
  disabled,
  variant,
  children
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  variant: 'neutral' | 'danger' | 'warn'
  children: React.ReactNode
}) {
  const hover =
    variant === 'danger'
      ? 'text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-950/40'
      : variant === 'warn'
        ? 'text-gray-500 hover:text-amber-700 hover:bg-amber-50 dark:text-gray-400 dark:hover:text-amber-400 dark:hover:bg-amber-950/30'
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${hover}`}
    >
      {children}
    </button>
  )
}

function extractBatchMeta(run: StudentAssessmentRun): { batchId: string | null; batchSize: number | null } {
  const metadata = (run.runMetadataJson && typeof run.runMetadataJson === 'object')
    ? (run.runMetadataJson as Record<string, unknown>)
    : null
  const batchId = metadata?.activationBatchId ? String(metadata.activationBatchId) : null
  const batchSizeRaw = metadata?.activationBatchSize
  const batchSize = typeof batchSizeRaw === 'number' ? batchSizeRaw : Number(batchSizeRaw || 0) || null
  return { batchId, batchSize }
}

export default function StudentAssessments() {
  const { user } = useAuth()
  const [scope, setScope] = useState<StudentAssessmentSelectionScope>('class')
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [groups, setGroups] = useState<GroupItem[]>([])
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const [students, setStudents] = useState<StudentOption[]>([])
  const [selectedStudentUnids, setSelectedStudentUnids] = useState<string[]>([])
  const [activationDetails, setActivationDetails] = useState<StudentAssessmentActivationDetails>(DEFAULT_DETAILS)
  const [showDetails, setShowDetails] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [runsLoading, setRunsLoading] = useState(false)
  const [runs, setRuns] = useState<StudentAssessmentRun[]>([])
  const [rowActionBusy, setRowActionBusy] = useState<Record<number, boolean>>({})
  const [batchActionBusy, setBatchActionBusy] = useState<Record<string, boolean>>({})
  const [selectedRunIds, setSelectedRunIds] = useState<number[]>([])
  const [bulkActionBusy, setBulkActionBusy] = useState(false)
  /** Sammeljobs: standard eingeklappt, nur Kopfzeile sichtbar */
  const [expandedBatches, setExpandedBatches] = useState<Record<string, boolean>>({})
  const [activationConflictOpen, setActivationConflictOpen] = useState(false)
  const [activationConflictList, setActivationConflictList] = useState<StudentAssessmentActivateConflictStudent[]>(
    []
  )
  const [activationConflictChoices, setActivationConflictChoices] = useState<
    Record<string, StudentAssessmentActivationConflictResolution>
  >({})

  const roleSet = useMemo(() => {
    const set = new Set<string>()
    if (user?.role) set.add(String(user.role).toLowerCase())
    ;(user?.roles || []).forEach((role) => role && set.add(String(role).toLowerCase()))
    ;(user?.allRoles || []).forEach((role) => role && set.add(String(role).toLowerCase()))
    return set
  }, [user])

  const hasAssessmentAccess = useMemo(() => {
    return Array.from(roleSet).some((role) =>
      ['teacher', 'lehrer', 'klassenlehrer', 'abteilungsleiter', 'schulleitung', 'admin', 'administrator'].includes(role)
    )
  }, [roleSet])

  const canSeeAllRuns = useMemo(() => {
    return ['admin', 'administrator', 'schulleitung'].some((role) => roleSet.has(role))
  }, [roleSet])

  const loadClasses = async () => {
    const response = await apiClient.get('/students/classes')
    const classRows = (response.data?.data || []) as ClassItem[]
    setClasses(classRows)
    if (classRows.length > 0) {
      setSelectedClass((prev) => prev || classRows[0].Klasse)
    }
  }

  const loadGroups = async () => {
    const response = await apiClient.get('/groups')
    const groupRows = (response.data?.data || []) as Array<{ id: number; name: string }>
    const mapped = groupRows.map((row) => ({ id: Number(row.id), name: String(row.name || `Gruppe ${row.id}`) }))
    setGroups(mapped)
    if (mapped.length > 0) {
      setSelectedGroupId((prev) => prev ?? mapped[0].id)
    }
  }

  const loadRuns = async (silent = false) => {
    if (!silent) setRunsLoading(true)
    try {
      const data = await studentAssessmentsApi.getRuns(100)
      setRuns(data)
    } catch (error: any) {
      if (!silent) {
        toast.error(error?.response?.data?.error || 'Jobliste konnte nicht geladen werden.')
      }
    } finally {
      if (!silent) setRunsLoading(false)
    }
  }

  const loadClassStudents = async (className: string) => {
    if (!className) {
      setStudents([])
      setSelectedStudentUnids([])
      return
    }
    try {
      const response = await apiClient.get(`/statistics/class/${encodeURIComponent(className)}/progress`)
      const rows = (response.data?.data || []) as Array<{ student: { ID: string; Vorname: string; Name: string; Klasse?: string } }>
      const options = rows
        .map((row) => ({
          unid: row.student.ID,
          label: `${row.student.Vorname} ${row.student.Name}`.trim(),
          className: className
        }))
        .filter((row) => row.unid)
      setStudents(options)
      setSelectedStudentUnids((prev) => prev.filter((unid) => options.some((option) => option.unid === unid)))
    } catch {
      setStudents([])
      setSelectedStudentUnids([])
      toast.error('Schülerliste konnte nicht geladen werden.')
    }
  }

  useEffect(() => {
    if (!hasAssessmentAccess) return
    Promise.all([loadClasses(), loadGroups(), loadRuns()]).catch(() => {
      toast.error('Lernstand-Automatik konnte nicht initialisiert werden.')
    })
  }, [hasAssessmentAccess])

  useEffect(() => {
    if (!hasAssessmentAccess) return
    if (scope === 'students') {
      loadClassStudents(selectedClass)
    }
  }, [scope, selectedClass, hasAssessmentAccess])

  useEffect(() => {
    if (!hasAssessmentAccess) return
    const interval = setInterval(() => {
      loadRuns(true)
    }, 15000)
    return () => clearInterval(interval)
  }, [hasAssessmentAccess])

  const toggleStudent = (unid: string) => {
    setSelectedStudentUnids((prev) => (prev.includes(unid) ? prev.filter((id) => id !== unid) : [...prev, unid]))
  }

  const activationDisabled = useMemo(() => {
    if (submitting) return true
    if (scope === 'class') return !selectedClass
    if (scope === 'group') return !selectedGroupId
    return selectedStudentUnids.length === 0
  }, [scope, selectedClass, selectedGroupId, selectedStudentUnids, submitting])

  const groupedRuns = useMemo(() => {
    const groups: Array<{ key: string; batchId: string | null; batchSize: number | null; runs: StudentAssessmentRun[] }> = []
    const byBatch = new Map<string, { key: string; batchId: string | null; batchSize: number | null; runs: StudentAssessmentRun[] }>()
    for (const run of runs) {
      const { batchId, batchSize } = extractBatchMeta(run)
      if (batchId) {
        const existing = byBatch.get(batchId)
        if (existing) {
          existing.runs.push(run)
          continue
        }
        const group = { key: `batch:${batchId}`, batchId, batchSize, runs: [run] }
        byBatch.set(batchId, group)
        groups.push(group)
        continue
      }
      groups.push({ key: `single:${run.id}`, batchId: null, batchSize: null, runs: [run] })
    }
    return groups
  }, [runs])

  useEffect(() => {
    const availableIds = new Set(runs.map((run) => run.id))
    setSelectedRunIds((prev) => prev.filter((id) => availableIds.has(id)))
  }, [runs])

  const allRunIds = useMemo(() => runs.map((run) => run.id), [runs])
  const allSelected = allRunIds.length > 0 && selectedRunIds.length === allRunIds.length

  const toggleRunSelection = (runId: number) => {
    setSelectedRunIds((prev) => (prev.includes(runId) ? prev.filter((id) => id !== runId) : [...prev, runId]))
  }

  const toggleSelectAllRuns = (checked: boolean) => {
    setSelectedRunIds(checked ? allRunIds : [])
  }

  const toggleSelectGroupRuns = (groupRunIds: number[], checked: boolean) => {
    setSelectedRunIds((prev) => {
      const set = new Set(prev)
      if (checked) {
        groupRunIds.forEach((id) => set.add(id))
      } else {
        groupRunIds.forEach((id) => set.delete(id))
      }
      return Array.from(set)
    })
  }

  const toggleBatchExpanded = (batchId: string) => {
    setExpandedBatches((prev) => ({ ...prev, [batchId]: !prev[batchId] }))
  }

  const buildActivationPayload = (): StudentAssessmentActivatePayload => {
    const payload: StudentAssessmentActivatePayload = {
      scope,
      details: activationDetails
    }
    if (scope === 'class') payload.className = selectedClass
    if (scope === 'group' && selectedGroupId != null) payload.groupId = selectedGroupId
    if (scope === 'students') {
      payload.className = selectedClass
      payload.studentUnids = selectedStudentUnids
    }
    return payload
  }

  const activateWithPayload = async (
    payload: StudentAssessmentActivatePayload,
    studentConflictResolutions?: Record<string, StudentAssessmentActivationConflictResolution>
  ) => {
    setSubmitting(true)
    try {
      const body: StudentAssessmentActivatePayload = studentConflictResolutions
        ? { ...payload, studentConflictResolutions }
        : payload
      const result = await studentAssessmentsApi.activate(body)
      setActivationConflictOpen(false)
      setActivationConflictList([])
      setActivationConflictChoices({})
      toast.success(`Aktiviert: ${result.activatedStudents} Schüler, ${result.queuedJobs} Jobs angelegt.`)
      if (result.skipped.length > 0) {
        const kp = result.skipped.filter((s) => s.reason === 'keep-previous-job').length
        const oq = result.skipped.filter((s) => s.reason === 'already-queued').length
        const parts: string[] = []
        if (kp > 0) parts.push(`${kp} bei bestehendem Job belassen`)
        if (oq > 0) parts.push(`${oq} bereits laufend/queued`)
        if (parts.length > 0) {
          toast(parts.join('; '), { icon: 'ℹ️' })
        }
      }
      await loadRuns(true)
    } catch (error: unknown) {
      if (error instanceof ActivateStudentJobConflictError) {
        const initial: Record<string, StudentAssessmentActivationConflictResolution> = {}
        error.conflicts.forEach((c) => {
          initial[c.studentUnid] = 'move_to_new'
        })
        setActivationConflictChoices(initial)
        setActivationConflictList(error.conflicts)
        setActivationConflictOpen(true)
        return
      }
      const err = error as { response?: { data?: { error?: string } } }
      toast.error(err?.response?.data?.error || 'Aktivierung fehlgeschlagen.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleActivate = async () => {
    if (activationDisabled) return
    if (!activationDetails.triggerOnSchedule && !activationDetails.triggerOnEvent) {
      toast.error('Bitte mindestens eine Auslöselogik auswählen (regelmäßig oder ereignisbasiert).')
      return
    }
    await activateWithPayload(buildActivationPayload())
  }

  const confirmActivationConflicts = () => {
    void activateWithPayload(buildActivationPayload(), activationConflictChoices)
  }

  const setAllConflictChoices = (choice: StudentAssessmentActivationConflictResolution) => {
    setActivationConflictChoices((prev) => {
      const next = { ...prev }
      activationConflictList.forEach((c) => {
        next[c.studentUnid] = choice
      })
      return next
    })
  }

  const setBusy = (runId: number, busy: boolean) => {
    setRowActionBusy((prev) => ({ ...prev, [runId]: busy }))
  }

  const handlePauseRun = async (runId: number) => {
    setBusy(runId, true)
    try {
      const result = await studentAssessmentsApi.pauseRun(runId)
      if (result.paused) {
        toast.success('Job pausiert.')
      } else {
        toast('Keine aktive Automatik-Konfiguration zum Pausieren gefunden.', { icon: 'ℹ️' })
      }
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Pausieren fehlgeschlagen.')
    } finally {
      setBusy(runId, false)
    }
  }

  const handleStopRun = async (runId: number) => {
    setBusy(runId, true)
    try {
      await studentAssessmentsApi.stopRun(runId)
      toast.success('Job beendet.')
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Beenden fehlgeschlagen.')
    } finally {
      setBusy(runId, false)
    }
  }

  const handleDeleteRun = async (runId: number) => {
    const confirmed = window.confirm('Diesen Job wirklich löschen?')
    if (!confirmed) return
    setBusy(runId, true)
    try {
      await studentAssessmentsApi.deleteRun(runId)
      toast.success('Job gelöscht.')
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Löschen fehlgeschlagen.')
    } finally {
      setBusy(runId, false)
    }
  }

  const setBatchBusy = (batchId: string, busy: boolean) => {
    setBatchActionBusy((prev) => ({ ...prev, [batchId]: busy }))
  }

  const handlePauseBatch = async (batchId: string) => {
    setBatchBusy(batchId, true)
    try {
      const result = await studentAssessmentsApi.pauseBatch(batchId)
      toast.success(`Sammeljob pausiert (${result.pausedCount || 0} konfiguriert).`)
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Sammeljob konnte nicht pausiert werden.')
    } finally {
      setBatchBusy(batchId, false)
    }
  }

  const handleStopBatch = async (batchId: string) => {
    setBatchBusy(batchId, true)
    try {
      const result = await studentAssessmentsApi.stopBatch(batchId)
      toast.success(`Sammeljob beendet (${result.stoppedCount || 0} Runs gestoppt).`)
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Sammeljob konnte nicht beendet werden.')
    } finally {
      setBatchBusy(batchId, false)
    }
  }

  const handleDeleteBatch = async (batchId: string) => {
    const confirmed = window.confirm('Diesen Sammeljob mit allen enthaltenen Runs wirklich löschen?')
    if (!confirmed) return
    setBatchBusy(batchId, true)
    try {
      const result = await studentAssessmentsApi.deleteBatch(batchId)
      toast.success(`Sammeljob gelöscht (${result.deletedRuns || 0} Runs).`)
      await loadRuns(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Sammeljob konnte nicht gelöscht werden.')
    } finally {
      setBatchBusy(batchId, false)
    }
  }

  const handleStopSelectedRuns = async () => {
    if (selectedRunIds.length === 0) return
    setBulkActionBusy(true)
    try {
      const results = await Promise.allSettled(selectedRunIds.map((runId) => studentAssessmentsApi.stopRun(runId)))
      const successCount = results.filter((result) => result.status === 'fulfilled').length
      const failedCount = results.length - successCount
      if (successCount > 0) {
        toast.success(`${successCount} Jobs beendet.`)
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} Jobs konnten nicht beendet werden.`)
      }
      setSelectedRunIds([])
      await loadRuns(true)
    } finally {
      setBulkActionBusy(false)
    }
  }

  const handleDeleteSelectedRuns = async () => {
    if (selectedRunIds.length === 0) return
    const confirmed = window.confirm(`${selectedRunIds.length} ausgewählte Jobs wirklich löschen?`)
    if (!confirmed) return
    setBulkActionBusy(true)
    try {
      const results = await Promise.allSettled(selectedRunIds.map((runId) => studentAssessmentsApi.deleteRun(runId)))
      const successCount = results.filter((result) => result.status === 'fulfilled').length
      const failedCount = results.length - successCount
      if (successCount > 0) {
        toast.success(`${successCount} Jobs gelöscht.`)
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} Jobs konnten nicht gelöscht werden.`)
      }
      setSelectedRunIds([])
      await loadRuns(true)
    } finally {
      setBulkActionBusy(false)
    }
  }

  if (!hasAssessmentAccess) {
    return (
      <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
        Diese Funktion ist für deine Rolle nicht freigeschaltet.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Lernstand-Automatik</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Auswahl treffen, aktivieren, Jobs werden direkt angelegt.
        </p>
      </div>

      <section className="card p-6">
        <ListToolbar
          title="Aktivierungsfluss"
          description="Wähle Gruppe, Klasse oder einzelne Schüler und aktiviere in einem Schritt."
          trailing={
            <Button variant="ghost" size="sm" onClick={() => setShowDetails((prev) => !prev)}>
              {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
            </Button>
          }
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="text-sm text-gray-700 dark:text-gray-200">
            <span className="mb-1 block">Modus</span>
            <select
              value={scope}
              onChange={(event) => {
                setScope(event.target.value as StudentAssessmentSelectionScope)
                setSelectedStudentUnids([])
              }}
              className="input-field min-h-[2.25rem] w-full py-2 text-sm"
            >
              <option value="group">Gruppe</option>
              <option value="class">Klasse</option>
              <option value="students">Einzelne Schüler</option>
            </select>
          </label>

          {scope === 'group' ? (
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="mb-1 block">Gruppe</span>
              <select
                value={selectedGroupId ?? ''}
                onChange={(event) => setSelectedGroupId(Number(event.target.value) || null)}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              >
                <option value="">-- auswählen --</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="mb-1 block">Klasse</span>
              <select
                value={selectedClass}
                onChange={(event) => setSelectedClass(event.target.value)}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              >
                <option value="">-- auswählen --</option>
                {classes.map((item) => (
                  <option key={item.Klasse} value={item.Klasse}>
                    {item.Klasse}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex items-end">
            <Button className="w-full" onClick={handleActivate} disabled={activationDisabled}>
              {submitting ? 'Aktiviere...' : 'Aktivieren'}
            </Button>
          </div>
        </div>

        {scope === 'students' ? (
          <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Schüler auswählen</p>
              <span className="text-xs text-gray-500 dark:text-gray-400">{selectedStudentUnids.length} gewählt</span>
            </div>
            {students.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Keine Schüler in der gewählten Klasse gefunden.</p>
            ) : (
              <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto pr-1 md:grid-cols-2">
                {students.map((student) => (
                  <label key={student.unid} className="flex items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={selectedStudentUnids.includes(student.unid)}
                      onChange={() => toggleStudent(student.unid)}
                    />
                    <span>{student.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {showDetails ? (
          <div className="mt-4 space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="mb-1 block">Intervall</span>
              <select
                value={activationDetails.scheduleInterval || 'monthly'}
                onChange={(event) => setActivationDetails((prev) => ({ ...prev, scheduleInterval: event.target.value as 'daily' | 'weekly' | 'monthly' }))}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
                disabled={!activationDetails.triggerOnSchedule}
              >
                <option value="monthly">1x pro Monat</option>
                <option value="weekly">1x pro Woche</option>
                <option value="daily">1x pro Tag</option>
              </select>
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="mb-1 block">Lookback (Tage)</span>
              <input
                type="number"
                min={14}
                max={365}
                value={Number(activationDetails.lookbackDays || 60)}
                onChange={(event) =>
                  setActivationDetails((prev) => ({
                    ...prev,
                    lookbackDays: Math.max(14, Math.min(365, Number(event.target.value) || 60))
                  }))
                }
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              />
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="mb-1 block">Min-Alarm</span>
              <select
                value={activationDetails.minAlertLevel || 'yellow'}
                onChange={(event) => setActivationDetails((prev) => ({ ...prev, minAlertLevel: event.target.value as StudentAssessmentAlertLevel }))}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              >
                <option value="red">Nur Rot</option>
                <option value="yellow">Ab Gelb</option>
                <option value="green">Ab Grün</option>
                <option value="gray">Alles inkl. Grau</option>
              </select>
            </label>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={Boolean(activationDetails.triggerOnSchedule ?? true)}
                  onChange={(event) => setActivationDetails((prev) => ({ ...prev, triggerOnSchedule: event.target.checked }))}
                />
                Regelmäßig neu auswerten (Intervall)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={Boolean(activationDetails.triggerOnEvent ?? true)}
                  onChange={(event) => setActivationDetails((prev) => ({ ...prev, triggerOnEvent: event.target.checked }))}
                />
                Ereignisbasiert neu auswerten
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ereignisbasierte Neubewertung: samstags wird geprüft, ob sich der Leistungsbeleg-Zuwachs eines Schülers
              im Vergleich zum Klassendurchschnitt signifikant nach oben oder unten verändert hat. In diesem Fall wird
              ein Trigger für eine Neubewertung erzeugt.
            </p>
          </div>
        ) : null}
      </section>

      <section className="card p-6">
        <ListToolbar
          title={canSeeAllRuns ? 'Alle aktivierten Jobs' : 'Meine aktivierten Jobs'}
          description="Verlauf der angelegten und laufenden Lernstandsjobs."
          trailing={
            <>
              <Button variant="outline" size="sm" onClick={() => loadRuns()} disabled={bulkActionBusy}>
                <span className="inline-flex items-center gap-1.5">
                  <RefreshCw className="h-4 w-4" aria-hidden />
                  Aktualisieren
                </span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleStopSelectedRuns}
                disabled={selectedRunIds.length === 0 || bulkActionBusy}
              >
                <span className="inline-flex items-center gap-1.5">
                  <StopCircle className="h-4 w-4" aria-hidden />
                  Ausgewählte stoppen ({selectedRunIds.length})
                </span>
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteSelectedRuns}
                disabled={selectedRunIds.length === 0 || bulkActionBusy}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Ausgewählte löschen
                </span>
              </Button>
            </>
          }
        />

        <TableScrollArea>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-14">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(event) => toggleSelectAllRuns(event.target.checked)}
                      aria-label="Alle Jobs auswählen"
                    />
                    <ListChecks className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={Clock} label="Zeit" />
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={Users} label="Klasse / Schüler" />
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={Activity} label="Status" />
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={AlertTriangle} label="Alarm" />
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={AlignLeft} label="Hauptgrund" />
                </TableHeaderCell>
                <TableHeaderCell>
                  <TableColumnHeading icon={User} label="Gestartet von" />
                </TableHeaderCell>
                <TableHeaderCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <MoreHorizontal className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                    <span>Aktionen</span>
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runsLoading ? (
                <EmptyTableRow colSpan={8} title="Lade Jobliste..." />
              ) : runs.length === 0 ? (
                <EmptyTableRow colSpan={8} title="Noch keine Jobs vorhanden" description="Nach der ersten Aktivierung erscheinen Jobs hier automatisch." />
              ) : (
                groupedRuns.map((group) => {
                  const batchId = group.batchId
                  const isBatch = Boolean(batchId)
                  const batchExpanded = isBatch ? Boolean(expandedBatches[batchId as string]) : true
                  const showStudentRows = !isBatch || batchExpanded

                  return (
                  <React.Fragment key={group.key}>
                    {isBatch ? (
                      <TableRow>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              className="-ml-1 h-8 w-8 shrink-0 p-0 text-gray-600 dark:text-gray-400"
                              onClick={() => toggleBatchExpanded(batchId as string)}
                              aria-expanded={batchExpanded}
                              aria-label={
                                batchExpanded ? 'Schüler im Sammeljob ausblenden' : 'Schüler im Sammeljob einblenden'
                              }
                            >
                              {batchExpanded ? (
                                <ChevronDown className="h-4 w-4" aria-hidden />
                              ) : (
                                <ChevronRight className="h-4 w-4" aria-hidden />
                              )}
                            </Button>
                            <input
                              type="checkbox"
                              checked={group.runs.every((run) => selectedRunIds.includes(run.id))}
                              onChange={(event) => toggleSelectGroupRuns(group.runs.map((run) => run.id), event.target.checked)}
                              aria-label="Sammeljob auswählen"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-gray-800 dark:text-gray-100">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>{new Date(group.runs[0].createdAt).toLocaleString('de-DE')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-gray-800 dark:text-gray-100">
                          <div className="flex items-center gap-2">
                            <Layers className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                            <span>
                              Sammeljob ({group.batchSize || group.runs.length} Schüler)
                              {group.runs[0]?.className ? ` — ${group.runs[0].className}` : ''}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                            <Badge variant="info">
                              {group.runs.filter((run) => run.status === 'completed').length}/{group.runs.length}{' '}
                              abgeschlossen
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                            <CircleDot className="h-4 w-4" aria-hidden />
                            —
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400">
                          <div className="flex items-start gap-2">
                            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>
                              {!batchExpanded ? 'Schülerzeilen eingeklappt' : `${group.runs.length} Einzeleinträge`}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100">
                            <User className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>{group.runs[0].initiatedByDisplayName || '—'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center justify-end gap-0.5">
                            <IconActionButton
                              title="Sammeljob pausieren"
                              variant="neutral"
                              disabled={Boolean(batchActionBusy[batchId as string])}
                              onClick={() => handlePauseBatch(batchId as string)}
                            >
                              <Pause className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                            <IconActionButton
                              title="Sammeljob beenden"
                              variant="warn"
                              disabled={Boolean(batchActionBusy[batchId as string])}
                              onClick={() => handleStopBatch(batchId as string)}
                            >
                              <StopCircle className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                            <IconActionButton
                              title="Sammeljob löschen"
                              variant="danger"
                              disabled={Boolean(batchActionBusy[batchId as string])}
                              onClick={() => handleDeleteBatch(batchId as string)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {showStudentRows
                      ? group.runs.map((run) => (
                      <TableRow key={run.id} className={isBatch ? 'bg-gray-50 dark:bg-gray-900/35' : undefined}>
                        <TableCell className={isBatch ? 'pl-10' : undefined}>
                          <input
                            type="checkbox"
                            checked={selectedRunIds.includes(run.id)}
                            onChange={() => toggleRunSelection(run.id)}
                            aria-label={`Job ${run.id} auswählen`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100">
                            <Clock className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>{new Date(run.createdAt).toLocaleString('de-DE')}</span>
                          </div>
                        </TableCell>
                        <TableCell className={isBatch ? 'pl-2 text-gray-800 dark:text-gray-100' : undefined}>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>
                              {isBatch ? '↳ ' : ''}
                              {run.className || '-'} / {run.studentDisplayName || run.studentUnid}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <StatusGlyph status={run.status} />
                            <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <AlertLevelGlyph level={run.alertLevel} />
                            <Badge variant={alertVariant(run.alertLevel)}>{run.alertLevel || '—'}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-700 dark:text-gray-300">
                          <div className="flex items-start gap-2">
                            <AlignLeft className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span title={run.mainIssue || undefined}>{run.mainIssue || '—'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100">
                            <User className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <span>{run.initiatedByDisplayName || '—'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center justify-end gap-0.5">
                            <IconActionButton
                              title="Pausieren"
                              variant="neutral"
                              disabled={Boolean(rowActionBusy[run.id]) || bulkActionBusy}
                              onClick={() => handlePauseRun(run.id)}
                            >
                              <Pause className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                            <IconActionButton
                              title="Beenden"
                              variant="warn"
                              disabled={
                                Boolean(rowActionBusy[run.id]) ||
                                bulkActionBusy ||
                                !['queued', 'processing'].includes(run.status)
                              }
                              onClick={() => handleStopRun(run.id)}
                            >
                              <StopCircle className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                            <IconActionButton
                              title="Löschen"
                              variant="danger"
                              disabled={Boolean(rowActionBusy[run.id]) || bulkActionBusy}
                              onClick={() => handleDeleteRun(run.id)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </IconActionButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                      : null}
                  </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableScrollArea>
      </section>

      <Modal
        isOpen={activationConflictOpen}
        onClose={() => !submitting && setActivationConflictOpen(false)}
        title="Schüler bereits in Aktivierungsjobs"
        size="xl"
        closeOnBackdrop={false}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setActivationConflictOpen(false)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button onClick={() => confirmActivationConflicts()} disabled={submitting}>
              {submitting ? 'Wird aktiviert…' : 'Mit Auswahl aktivieren'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm">
          <p className="text-gray-700 dark:text-gray-300">
            Pro Schüler ist nur{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">ein Aktivierungsjob</span>{' '}
            unter deinem Konto möglich. Für betroffene Schüler:innen wähle, ob ihre bisherigen
            Aktivierungs-<strong>Läufe</strong> (inkl. Sammeljob-Einträge) zugunsten dieser neuen
            Aktivierung entfernt werden oder ob sie beim bestehenden Stand belassen werden.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="xs" type="button" onClick={() => setAllConflictChoices('move_to_new')}>
              Alle: in neuen Job
            </Button>
            <Button variant="outline" size="xs" type="button" onClick={() => setAllConflictChoices('keep_previous')}>
              Alle: bei bestehendem belassen
            </Button>
          </div>
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Schüler</TableHeaderCell>
                  <TableHeaderCell>Klasse</TableHeaderCell>
                  <TableHeaderCell>Bestehend</TableHeaderCell>
                  <TableHeaderCell>Auswahl</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activationConflictList.map((row) => {
                  const hint: string[] = []
                  if (row.existingBatchIds.length > 0) {
                    hint.push(`${row.existingBatchIds.length} Sammeljob(s)`)
                  }
                  if (row.legacyActivationRunIds.length > 0) {
                    hint.push(`${row.legacyActivationRunIds.length} ältere Lauf/Läufe`)
                  }
                  return (
                    <TableRow key={row.studentUnid}>
                      <TableCell className="font-medium">{row.studentDisplayName}</TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">{row.className || '—'}</TableCell>
                      <TableCell className="max-w-[12rem] text-gray-600 dark:text-gray-400">{hint.join(', ') || '—'}</TableCell>
                      <TableCell className="min-w-[280px]">
                        <fieldset className="space-y-2 border-none p-0">
                          <legend className="sr-only">Auflösung für {row.studentDisplayName}</legend>
                          <label className="flex cursor-pointer items-start gap-2 text-gray-800 dark:text-gray-200">
                            <input
                              type="radio"
                              className="mt-0.5 shrink-0"
                              name={`conflict-choice-${row.studentUnid}`}
                              checked={activationConflictChoices[row.studentUnid] === 'move_to_new'}
                              onChange={() =>
                                setActivationConflictChoices((prev) => ({
                                  ...prev,
                                  [row.studentUnid]: 'move_to_new'
                                }))
                              }
                            />
                            <span>
                              In neuen Job (bisherige Aktivierungsläufe werden gelöscht)
                            </span>
                          </label>
                          <label className="flex cursor-pointer items-start gap-2 text-gray-800 dark:text-gray-200">
                            <input
                              type="radio"
                              className="mt-0.5 shrink-0"
                              name={`conflict-choice-${row.studentUnid}`}
                              checked={activationConflictChoices[row.studentUnid] === 'keep_previous'}
                              onChange={() =>
                                setActivationConflictChoices((prev) => ({
                                  ...prev,
                                  [row.studentUnid]: 'keep_previous'
                                }))
                              }
                            />
                            <span>
                              Bei bestehendem Aktivierungsjob belassen (erscheint nicht in diesem neuen Lauf)
                            </span>
                          </label>
                        </fieldset>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableScrollArea>
        </div>
      </Modal>
    </div>
  )
}
